import type { EventType, HtmlType } from '../../types.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { LeafBlock } from '../leaf-block.js'
import { ScreenEditorInput } from '../decorators/screen-editor-input.js'
import { Expression } from '../../types.js'
import type { ImageSelectorOutput } from '../../app-types.js'
import { Parameter } from '../../script/parameter.js'

export const ImageSelectorModes = {
	image: 'Image',
	document: 'Document',
	qrCode: 'QR Code',
} as const
export type ImageSelectorMode = keyof typeof ImageSelectorModes

export const ImageSelectorImageFormats = {
	png: 'PNG',
	jpg: 'JPEG',
} as const
export type ImageSelectorImageFormat = keyof typeof ImageSelectorImageFormats

@jsonClass('ImageSelectorBlock')
export class ImageSelectorBlock extends LeafBlock {
	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 1,
		label: 'Selection mode',
		options: Object.entries(ImageSelectorModes),
	})
	mode: ImageSelectorMode = 'image'

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 2,
		label: 'Image format',
		options: Object.entries(ImageSelectorImageFormats),
	})
	imageFormat: ImageSelectorImageFormat = 'jpg'

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 3,
		label: 'Allow file selection',
		codeLanguage: 'ts',
		tsReturnType: () => 'boolean',
		editorSize: 'singleline',
		isBinding: false,
	})
	allowFileSelectionCode = new Expression('true')

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 3,
		label: 'Allowed file MIME ty[pes',
		codeLanguage: 'ts',
		tsReturnType: () => 'string[] | undefined',
		editorSize: 'singleline',
		isBinding: false,
	})
	allowedFileMimeTypesCode = new Expression('undefined')

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 5,
		label: 'Allow camera',
		codeLanguage: 'ts',
		tsReturnType: () => 'boolean',
		editorSize: 'singleline',
		isBinding: false,
	})
	allowCameraCode = new Expression('true')

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 6,
		label: 'Auto start capturing',
		codeLanguage: 'ts',
		tsReturnType: () => 'boolean',
		editorSize: 'singleline',
		isBinding: false,
		isVisible: (blocks) =>
			(blocks[0] as ImageSelectorBlock).allowCameraCode.content != 'false',
	})
	autoStartCapturingCode = new Expression('false')

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 7,
		label: 'Requested image width',
		codeLanguage: 'ts',
		tsReturnType: () => 'number | undefined',
		editorSize: 'singleline',
		isBinding: false,
	})
	requestedImageWidthCode = new Expression('')

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 8,
		label: 'Requested image height',
		codeLanguage: 'ts',
		tsReturnType: () => 'number | undefined',
		editorSize: 'singleline',
		isBinding: false,
	})
	requestedImageHeightCode = new Expression('')

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 7,
		label: 'Auto use image',
		codeLanguage: 'ts',
		tsReturnType: () => 'boolean',
		editorSize: 'singleline',
		isBinding: false,
		isVisible: (blocks) =>
			(blocks[0] as ImageSelectorBlock).allowCameraCode.content != 'false' &&
			(blocks[0] as ImageSelectorBlock).mode != 'document',
	})
	autoUseImageCode = new Expression('false')

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 8,
		label: 'Take picture on QR detection',
		codeLanguage: 'ts',
		tsReturnType: () => 'boolean',
		editorSize: 'singleline',
		isBinding: false,
		isVisible: (blocks) => (blocks[0] as ImageSelectorBlock).mode == 'qrCode',
	})
	takePictureOnQrDetectionCode = new Expression('true')

	constructor(init: Partial<ImageSelectorBlock> = {}) {
		super()
		this.exampleText = 'Image selector'
		this.init(init)
	}

	producePreview(): HtmlType {
		return `<div style="background: #dddddd; border: 1px solid #bbbbbb; padding: 3em;">Image selector</div>`
	}

	getEditorCategory(): string {
		return 'Other'
	}

	get allowedEventTypes(): EventType[] {
		const outputType = 'Sys.Types.ImageSelectorOutput'
		return [
			{
				name: 'qrCodeUsed',
				parameters: [
					new Parameter({
						name: 'qrCode',
						type: 'string',
						defaultExpression: '$event',
					}),
				],
			},
			{
				name: 'imageUsed',
				parameters: [
					new Parameter({
						name: 'image',
						type: outputType,
						defaultExpression: '$event',
					}),
				],
			},
			{
				name: 'documentUsed',
				parameters: [
					new Parameter({
						name: 'document',
						type: outputType,
						defaultExpression: '$event',
					}),
				],
			},
		]
	}
}
